import { useTranslation } from 'react-i18next'
import { Mail, Phone, MapPin, Clock, Linkedin } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

export function Footer() {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const companyLinks = [
    { label: t('nav.about', 'Chi Siamo'), href: 'about' },
    { label: t('nav.products', 'Prodotti'), href: 'products' },
    { label: t('nav.partners', 'Partners'), href: 'partners' },
    { label: t('nav.contacts', 'Contatti'), href: 'contacts' },
  ]

  const serviceLinks = [
    { label: t('nav.technicalAssistance', 'Assistenza Tecnica'), href: 'technicalAssistance' },
    { label: t('nav.equipmentSales', 'Vendita Strumentazione'), href: 'equipmentSales' },
    { label: t('nav.pumpRepair', 'Riparazione Pompe'), href: 'pumpRepair' },
    { label: t('nav.calmix', 'Calmix'), href: 'calmix' },
  ]

  const legalLinks = [
    { label: t('footer.privacy', 'Privacy Policy'), href: 'privacy' },
    { label: t('footer.cookiePolicy', 'Cookie Policy'), href: 'privacy' },
  ]

  return (
    <footer style={{ backgroundColor: '#1a5a8a' }} className="text-white">
      <div className="container-main">
        <div className="py-12 lg:py-16">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <span className="font-heading text-xl font-bold text-white mb-6 block">
                Lab Instruments
              </span>
              <p className="mb-6 text-sm text-white/70">
                {t('footer.tagline', 'Eccellenza italiana per la strumentazione scientifica')}
              </p>
              <div className="space-y-3 text-sm text-white/70">
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-light" />
                  <span>
                    {t('contact.address', 'Via...')}<br />
                    {t('contact.city', 'Italia')}
                  </span>
                </div>
                <a
                  href={`tel:${t('contact.phone', '')}`}
                  className="flex items-center gap-3 hover:text-white transition-colors"
                >
                  <Phone className="h-4 w-4 flex-shrink-0 text-accent-light" />
                  {t('contact.phone', '')}
                </a>
                <a
                  href={`mailto:${t('contact.email', 'info@labinstruments.it')}`}
                  className="flex items-center gap-3 hover:text-white transition-colors"
                >
                  <Mail className="h-4 w-4 flex-shrink-0 text-accent-light" />
                  {t('contact.email', 'info@labinstruments.it')}
                </a>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 flex-shrink-0 text-accent-light" />
                  <span>{t('contact.hours', 'Lun-Ven 9:00-18:00')}</span>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-accent hover:text-white transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
                {t('footer.company', 'Azienda')}
              </h3>
              <ul className="space-y-2">
                {companyLinks.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink
                      to={link.href}
                      className="text-sm text-white/70 hover:text-accent-light transition-colors"
                    >
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service Links */}
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
                {t('nav.services', 'Servizi')}
              </h3>
              <ul className="space-y-2">
                {serviceLinks.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink
                      to={link.href}
                      className="text-sm text-white/70 hover:text-accent-light transition-colors"
                    >
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
                {t('footer.legalNotes', 'Note Legali')}
              </h3>
              <ul className="space-y-2">
                {legalLinks.map((link) => (
                  <li key={link.label}>
                    <LocalizedLink
                      to={link.href}
                      className="text-sm text-white/70 hover:text-accent-light transition-colors"
                    >
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 py-6">
          <div className="flex flex-col items-center justify-between gap-4 text-center text-sm text-white/50 sm:flex-row sm:text-left">
            <p>
              &copy; {currentYear} Lab Instruments. {t('footer.allRightsReserved')}
            </p>
            <p>
              P.IVA: ---
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
